import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TruckIcon, Shield, HeadphonesIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Static company info
const companyInfo = {
  tagline: "Your Trusted Business Supply Partner"
};

// Static testimonials
const testimonials = [
  {
    id: 1,
    name: "Sarah Johnson",
    company: "Tech Solutions Inc.",
    text: "USKE Supply has been our go-to vendor for office supplies. Their quality and service are unmatched.",
    rating: 5
  },
  {
    id: 2,
    name: "Michael Chen",
    company: "Green Valley Enterprises",
    text: "Fast delivery and excellent product quality. Highly recommend for any business.",
    rating: 5
  },
  {
    id: 3,
    name: "Emily Davis",
    company: "Creative Studios LLC",
    text: "The best prices and widest selection of office furniture we've found. Great customer service too!",
    rating: 5
  }
];

// Category icons mapping
const categoryIcons = {
  'office-supplies': '📎',
  'paper': '📄',
  'furniture': '🪑',
  'technology': '💻',
  'ink-toner': '🖨️',
  'groceries': '🛒',
  'cleaning': '🧹'
};

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [displaySettings, setDisplaySettings] = useState({
    show_prices: true,
    show_stock: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes, settingsRes] = await Promise.all([
        axios.get(`${API}/products?limit=8`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/settings`)
      ]);
      setFeaturedProducts(productsRes.data);
      setCategories(categoriesRes.data);
      setDisplaySettings(settingsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 via-purple-50 to-slate-100 py-12 sm:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold text-slate-800 mb-4 sm:mb-6">
              {companyInfo.tagline}
            </h1>
            <p className="text-base sm:text-xl text-slate-600 mb-6 sm:mb-8 leading-relaxed px-4">
              Premium office supplies, furniture, technology, and cleaning products for businesses of all sizes.
              Quality products delivered to your door.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
              <Link to="/products" className="w-full sm:w-auto">
                <Button size="lg" className="w-full sm:w-auto bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transition-all">
                  Shop All Products
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact" className="w-full sm:w-auto">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-purple-700 text-purple-700 hover:bg-purple-700 hover:text-white transition-all">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-10 sm:py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-8">
            <Card className="border-2 hover:shadow-lg transition-shadow">
              <CardContent className="p-4 sm:p-6 text-center">
                <div className="bg-blue-100 w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <TruckIcon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
                </div>
                <h3 className="font-semibold text-base sm:text-lg mb-2 text-slate-800">Fast Delivery</h3>
                <p className="text-slate-600 text-sm sm:text-base">Quick and reliable shipping to your business location</p>
              </CardContent>
            </Card>
            <Card className="border-2 hover:shadow-lg transition-shadow">
              <CardContent className="p-4 sm:p-6 text-center">
                <div className="bg-green-100 w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <Shield className="h-6 w-6 sm:h-8 sm:w-8 text-green-600" />
                </div>
                <h3 className="font-semibold text-base sm:text-lg mb-2 text-slate-800">Quality Guaranteed</h3>
                <p className="text-slate-600 text-sm sm:text-base">Premium products from trusted brands</p>
              </CardContent>
            </Card>
            <Card className="border-2 hover:shadow-lg transition-shadow">
              <CardContent className="p-4 sm:p-6 text-center">
                <div className="bg-purple-100 w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <HeadphonesIcon className="h-6 w-6 sm:h-8 sm:w-8 text-purple-600" />
                </div>
                <h3 className="font-semibold text-base sm:text-lg mb-2 text-slate-800">24/7 Support</h3>
                <p className="text-slate-600 text-sm sm:text-base">Dedicated customer service team ready to help</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-10 sm:py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-8 sm:mb-12 text-slate-800">Shop by Category</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
            {categories.map(category => (
              <Link
                key={category.id}
                to={`/category/${category.slug}`}
                className="group"
              >
                <Card className="h-full hover:shadow-xl transition-all duration-300 border-2 hover:border-purple-500">
                  <CardContent className="p-3 sm:p-6 text-center">
                    <div className="bg-gradient-to-br from-purple-50 to-slate-50 w-14 h-14 sm:w-20 sm:h-20 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-4 group-hover:from-purple-100 group-hover:to-slate-100 transition-colors">
                      <div className="text-2xl sm:text-3xl">{categoryIcons[category.slug] || '📦'}</div>
                    </div>
                    <h3 className="font-semibold text-xs sm:text-base text-slate-800 group-hover:text-purple-600 transition-colors">
                      {category.name}
                    </h3>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-10 sm:py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-800">Featured Products</h2>
            <Link to="/products">
              <Button variant="outline" className="border-slate-300 text-xs sm:text-sm">
                View All
                <ArrowRight className="ml-1 sm:ml-2 h-3 w-3 sm:h-4 sm:w-4" />
              </Button>
            </Link>
          </div>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            </div>
          ) : featuredProducts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-500 text-lg">No products available yet.</p>
              <p className="text-slate-400 text-sm mt-2">Check back soon for our latest offerings!</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
              {featuredProducts.map(product => (
                <Link key={product.id} to={`/product/${product.id}`} className="group">
                  <Card className="h-full hover:shadow-xl transition-all duration-300 border-2 hover:border-purple-500">
                    <CardContent className="p-2 sm:p-4">
                      <div className="aspect-square bg-slate-100 rounded-lg mb-2 sm:mb-4 overflow-hidden">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <h3 className="font-semibold text-xs sm:text-base text-slate-800 mb-1 sm:mb-2 line-clamp-2 group-hover:text-purple-600 transition-colors">
                        {product.name}
                      </h3>
                      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-1">
                        {displaySettings.show_prices ? (
                          <span className="text-base sm:text-xl font-bold text-purple-600">${(product.retail_price ?? product.price)}</span>
                        ) : (
                          <span className="text-xs sm:text-sm text-slate-500 italic">Contact for pricing</span>
                        )}
                        {displaySettings.show_stock ? (
                          <span className={`text-xs sm:text-sm ${product.stock > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {product.stock > 0 ? `${product.stock} in stock` : 'Out of Stock'}
                          </span>
                        ) : (
                          <span className={`text-xs sm:text-sm ${product.stock > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {product.stock > 0 ? 'In Stock' : 'Out of Stock'}
                          </span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-10 sm:py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-8 sm:mb-12 text-slate-800">What Our Customers Say</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-8">
            {testimonials.map(testimonial => (
              <Card key={testimonial.id} className="border-2">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex mb-3 sm:mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <span key={i} className="text-yellow-400 text-lg sm:text-xl">★</span>
                    ))}
                  </div>
                  <p className="text-slate-600 mb-3 sm:mb-4 italic text-sm sm:text-base">"{testimonial.text}"</p>
                  <div>
                    <p className="font-semibold text-slate-800 text-sm sm:text-base">{testimonial.name}</p>
                    <p className="text-xs sm:text-sm text-slate-500">{testimonial.company}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-10 sm:py-16 bg-gradient-to-r from-purple-600 via-blue-600 to-purple-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4">Ready to Get Started?</h2>
          <p className="text-base sm:text-xl mb-6 sm:mb-8 opacity-90">
            Browse our complete catalog and find everything your business needs
          </p>
          <Link to="/products">
            <Button size="lg" className="bg-white text-purple-600 hover:bg-slate-100 shadow-lg hover:shadow-xl transition-all">
              Shop Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
